<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2SetupPickerList from '@/components/v2/store-setup/shared/V2SetupPickerList.vue';
  import { confirmReservationBranchLeave } from '@/composition/reservations/reservation-branch-leave';
  import type { IPickerOption } from '@/domain/store-setup/location-step';
  import { IBranch } from '@/interfaces/IBranch';
  import { vueData } from '@/VueData';

  /** Lists this long get a search box. */
  const SEARCHABLE_FROM = 9;

  /**
   * The current branch (plan §4.2): a neutral outline pill that opens the
   * list of active branches. Changing it asks first when a reservation form
   * would be left behind.
   */
  const props = withDefaults(
    defineProps<{
      branches: IBranch[];
      branch: IBranch | null;
      /** Shown before the name (the context bar's «Sucursal:»). */
      prefix?: string;
      /** Fills its container (the drawer row). */
      block?: boolean;
    }>(),
    { prefix: undefined, block: false }
  );

  const options = computed<IPickerOption[]>(() =>
    props.branches.map((branch) => ({
      value: String(branch.id),
      label: branch.name,
    }))
  );

  async function select(value: string, close: () => void) {
    close();
    const target = props.branches.find((branch) => branch.id === Number(value));
    if (!target || target.id === props.branch?.id) return;
    if (await confirmReservationBranchLeave()) vueData.setCurrentBranch(target);
  }
</script>

<template>
  <PosUiPopover
    placement="bottom"
    align="start"
    class="v2-shell-branch-picker"
    :class="{ 'is-block': block }"
    :panel-class="block ? 'branch-panel-block' : ''"
  >
    <template #trigger="{ open, toggle }">
      <button
        type="button"
        class="picker-trigger"
        aria-haspopup="listbox"
        :aria-expanded="String(open)"
        @click="toggle"
      >
        <PosUiIcon name="storefront-outline" class="trigger-icon" />
        <span class="trigger-label">
          <span v-if="prefix" class="trigger-prefix">{{ prefix }}:</span>
          {{ branch ? branch.name : '—' }}
        </span>
        <PosUiIcon name="chevron-down" class="trigger-chevron" />
      </button>
    </template>
    <template #default="{ close }">
      <V2SetupPickerList
        :options="options"
        :value="branch ? String(branch.id) : null"
        :searchable="branches.length >= SEARCHABLE_FROM"
        :search-label="$t('shell.branch.search')"
        :list-label="$t('shell.branch.list')"
        :empty-text="$t('shell.branch.empty')"
        @select="select($event, close)"
      />
    </template>
  </PosUiPopover>
</template>

<style scoped lang="scss">
  .v2-shell-branch-picker {
    @apply tw-max-w-full;

    &.is-block {
      @apply tw-flex tw-w-full;

      .picker-trigger {
        @apply tw-w-full;
      }

      // In the drawer: as wide as the trigger. The drawer's transform makes
      // it the fixed panel's containing block, and it clips (overflow
      // hidden) anything wider than its 256 px.
      :deep(.pos-ui-popover-panel.branch-panel-block) {
        width: 231px;
      }
    }

    .picker-trigger {
      @apply tw-flex tw-h-11 tw-min-w-0 tw-max-w-full tw-cursor-pointer tw-items-center tw-rounded-lg tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-800 tw-transition-colors tw-duration-150;

      &:hover {
        @apply tw-bg-neutral-50;
      }

      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }

    .trigger-icon {
      @apply tw-mr-2 tw-shrink-0 tw-text-lg tw-text-neutral-500;
    }

    .trigger-label {
      @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-left;
    }

    .trigger-prefix {
      @apply tw-mr-1 tw-font-normal tw-text-neutral-500;
    }

    .trigger-chevron {
      @apply tw-ml-2 tw-shrink-0 tw-text-lg tw-text-neutral-500;
    }
  }
</style>
